import { store } from 'redux/store';
import { FC, useEffect, useState } from 'react';
import { State } from 'redux/initialState';
import { useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';

import { useGetBoardsSectionPermissions } from 'modules/boards/hooks/useGetBoardsSectionPermissions';
import { useGetDocuments } from 'modules/_shared/hooks/useGetDocuments';
import { addBoardDocuments } from 'modules/boards/utils';
import { updateBoard } from 'modules/boards/redux/boardActions';

import asyncActionSwal from 'utils/asyncActionSwal';
import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';
import downloadDocument from 'utils/downloadDocument';
import { Document } from 'types';

import BoardDetailsDocumentsCardHeader from './components/BoardDetailsDocumentCardHeader';
import BoardDetailsDocumentsCardBody from './components/BoardDetailsDocumentCardBody';
import CustomLoading from '../CustomLoading';
import { boardEntityTypes } from '../BoardsListHeader/constants/boardEntityTypes';
import { handleRemoveBasicBoardDocument } from './utils/handleRemoveBasicBoardDocument';

type BoardDetailsDocumentsCardProps = {
  boardId: string | undefined;
  boardDocumentsIds: string[];
  boardType: string;
};

const BoardDetailsDocumentsCard: FC<BoardDetailsDocumentsCardProps> = ({
  boardId,
  boardDocumentsIds,
  boardType,
}) => {
  const { t } = useTranslate();

  const user = useSelector((state: State) => state.user);
  const society = useSelector((state: State) => state.society?.actualSociety);

  const { isAdmin } = useGetBoardsSectionPermissions().data;

  const useGetDocumentsResult = useGetDocuments({
    documentIds: boardDocumentsIds,
  });

  const { documents: boardDocuments } = useGetDocumentsResult.data;
  const { isLoading: isLoadingDocuments } = useGetDocumentsResult;

  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleDownloadDocument = (index: number) => {
    downloadDocument({
      documentId: documents[index]?.['_id'],
      documentName: documents[index]?.name,
      documents: [],
    });
  };

  const handleRemoveDocument = async (index: number) => {
    const documentId = documents[index]?._id;
    const documentName = documents[index]?.name;

    asyncActionSwal({
      swalTitle: t('AreYouSureAboutDelDocument'),
      swalElementName: documentName,
      swalInfoText: t('ThisProcedureCantUndo'),
      swalConfirmButtonText: t('OkDelete'),
      callback: async () => {
        try {
          setIsLoading(true);
          switch (boardType) {
            case boardEntityTypes.BASIC_BOARD:
              if (boardId) {
                await handleRemoveBasicBoardDocument({
                  boardId,
                  documentId,
                  boardDocumentsIds,
                });
              }
              break;
            default:
              break;
          }
          successSwal(t('DocumentDeletedSuccessfully'));
        } catch (error) {
          errorSwal(t('ErrorRemovingDocument'));
        } finally {
          setIsLoading(false);
        }
      },
    });
  };

  const handleChangeFiles = async (newFiles: File[]) => {
    try {
      setIsLoading(true);

      const uploadedDocuments = await addBoardDocuments({
        files: newFiles,
        user,
        society,
      });

      const updatedDocuments = [...documents, ...uploadedDocuments];

      const updatedDocumentsIds = updatedDocuments.map(
        (document) => document._id
      );

      const updatedData = {
        attachedDocuments: updatedDocumentsIds,
      };

      await store.dispatch(updateBoard(boardId, updatedData, false));

      setDocuments(updatedDocuments);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (boardDocuments) {
      setDocuments(boardDocuments);
    }
  }, [boardDocuments]);

  return (
    <>
      <section className="board-member-basic-info-card col mb-5">
        <BoardDetailsDocumentsCardHeader
          isAdmin={isAdmin}
          onChangeFiles={handleChangeFiles}
        />

        <BoardDetailsDocumentsCardBody
          isAdmin={isAdmin}
          documents={documents}
          documentsCount={boardDocumentsIds?.length}
          isLoadingDocuments={isLoadingDocuments}
          onDownloadDocument={handleDownloadDocument}
          onRemoveDocument={handleRemoveDocument}
        />
      </section>

      {isLoading && <CustomLoading />}
    </>
  );
};

export default BoardDetailsDocumentsCard;
